/**
 * ExtractReviewPanes — docType-specific review surfaces for the extract viewer.
 *
 * The base viewer renders every extract adaptively (Fields · Text · JSON ·
 * Images). A few docTypes now land RICHER structured extracts that deserve a
 * dedicated "Review" tab instead of the generic field list:
 *
 *   • Note (Promissory Note) → `NoteReviewPane`
 *       - the wrapper-level quality strip (scan_quality / illegible_fields /
 *         expected_note_count_met)
 *       - the independent `terms_check` verification verdict badge
 *   • Photo Identification    → `IdReviewPane`  (per-ID vision cards + quality flags)
 *   • Background Report        → `BackgroundReviewPane` (per-section LOE conditioning)
 *
 * All field access is DEFENSIVE — these shapes come from backend extraction docs
 * and legacy rows differ (e.g. the note payload nests under `extract` on new
 * rows, top-level on old ones). Anything unknown is skipped, never rendered as
 * "false" or "[object Object]".
 *
 * Backend references (../eastview-os/frontend_documentation/):
 *   note-terms-verification.md · id-and-background-extracts.md
 */
import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { amber, green, grey, red } from "@mui/material/colors";

import { losExtractsApi } from "../../../../../api/losExtractsApi";
import { ImageThumb, T } from "./LoanExtractsViewer";
import { unwrapExtractPayload } from "./structured/normalizeExtract";

// ── docType → review kind ────────────────────────────────────────────────────
// Forgiving match against the extraction docType. Notes match "…note" (ends
// with note, so "Notice"/"Note of …" don't collide); IDs and background reports
// match their named docTypes. Returns null → no specialized tab (base tabs only).
export const reviewKindForDocType = (docType) => {
  const d = String(docType || "")
    .toLowerCase()
    .trim();
  if (!d) return null;
  if (/photo\s*id|identification|driver\s*licen|passport/.test(d)) return "id";
  if (/background/.test(d)) return "background";
  if (/promissory|note$/.test(d)) return "note";
  return null;
};

// ── shared primitives ────────────────────────────────────────────────────────
const humanize = (k) => {
  const s = String(k)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const Tag = ({ label, tone = "neutral", title }) => {
  const tones = {
    ok: { bg: green[50], fg: green[800], bd: green[200] },
    warn: { bg: amber[50], fg: amber[900], bd: amber[300] },
    danger: { bg: red[50], fg: red[800], bd: red[200] },
    neutral: { bg: grey[100], fg: grey[800], bd: grey[300] },
  };
  const c = tones[tone] || tones.neutral;
  const chip = (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        fontSize: 11.5,
        fontWeight: 600,
        lineHeight: 1.4,
        px: 1,
        py: "2px",
        borderRadius: "6px",
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.bd}`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
  return title ? (
    <Tooltip title={title}>{chip}</Tooltip>
  ) : (
    chip
  );
};

const Row = ({ label, value }) => (
  <Box sx={{ display: "flex", gap: 1.25, py: 0.65, alignItems: "flex-start" }}>
    <Box sx={{ width: 150, flex: "0 0 auto", fontSize: 12.5, color: T.sub }}>
      {label}
    </Box>
    <Box
      sx={{
        fontSize: 13,
        color: T.ink,
        wordBreak: "break-word",
        minWidth: 0,
        fontWeight: 500,
      }}
    >
      {value}
    </Box>
  </Box>
);

const Card = ({ title, right, children, sx }) => (
  <Box
    sx={{
      background: "#fff",
      border: `1px solid ${T.line}`,
      borderRadius: "12px",
      p: 2,
      boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
      ...sx,
    }}
  >
    {(title || right) && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.25,
          flexWrap: "wrap",
        }}
      >
        {title ? (
          <Box sx={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>
            {title}
          </Box>
        ) : (
          <span />
        )}
        {right}
      </Box>
    )}
    {children}
  </Box>
);

const SectionShell = ({ children }) => (
  <Box
    sx={{
      flex: 1,
      overflowY: "auto",
      minHeight: 0,
      p: 1.75,
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      background: T.bg,
    }}
  >
    {children}
  </Box>
);

// ── data hook: lazily fetch + parse the structured JSON (same path as Fields) ──
const useParsedExtract = (dealId, blobName) => {
  const [state, setState] = useState({ loading: true, json: undefined });

  useEffect(() => {
    if (!dealId || !blobName) {
      setState({ loading: false, json: undefined });
      return undefined;
    }
    let active = true;
    setState({ loading: true, json: undefined });
    losExtractsApi
      .getText(dealId, blobName)
      .then((t) => {
        if (!active) return;
        try {
          setState({
            loading: false,
            // Shape-A files keep the data under `payload` (EXTRACT_VIEWER.md §5).
            json: unwrapExtractPayload(typeof t === "string" ? JSON.parse(t) : t),
          });
        } catch {
          setState({ loading: false, json: undefined });
        }
      })
      .catch(() => active && setState({ loading: false, json: undefined }));
    return () => {
      active = false;
    };
  }, [dealId, blobName]);

  return state;
};

// ═══ NOTE ════════════════════════════════════════════════════════════════════
const TermsCheckBadge = ({ termsCheck }) => {
  if (!termsCheck || typeof termsCheck !== "object") return null;
  const { status, agreementRate, disagreements } = termsCheck;
  const pct =
    typeof agreementRate === "number"
      ? `${Math.round(agreementRate * 100)}%`
      : null;
  const nDisagree = Array.isArray(disagreements) ? disagreements.length : null;

  let tone = "neutral";
  let label = "Terms check: needs human review";
  if (status === "verified") {
    tone = "ok";
    label = pct ? `Independently verified (${pct})` : "Independently verified";
  } else if (status === "disagreements") {
    tone = "warn";
    label =
      nDisagree != null
        ? `${nDisagree} disagreement${nDisagree === 1 ? "" : "s"} — review`
        : "Disagreements — review";
  } else if (status === "needs_human_review") {
    tone = "danger";
    label = "Needs human review";
  } else if (status) {
    label = `Terms check: ${humanize(status)}`;
  }

  const detail = termsCheck.checkerModel
    ? `Blind-verified by ${termsCheck.checkerModel}${
        termsCheck.pagesReviewed ? ` · ${termsCheck.pagesReviewed} pages` : ""
      }`
    : undefined;

  return <Tag label={label} tone={tone} title={detail} />;
};

const NoteDisagreements = ({ disagreements }) => {
  if (!Array.isArray(disagreements) || !disagreements.length) return null;
  return (
    <Card title="Verification disagreements">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {disagreements.map((d, i) => (
          <Box
            key={i}
            sx={{
              border: `1px solid ${T.line}`,
              borderRadius: "8px",
              p: 1.25,
              background: amber[50],
            }}
          >
            <Box sx={{ fontSize: 12.5, fontWeight: 700, color: T.ink, mb: 0.5 }}>
              {humanize(d.field || `Field ${i + 1}`)}
            </Box>
            <Row label="Extract value" value={fmtVal(d.extractValue)} />
            <Row label="Checker value" value={fmtVal(d.checkerValue)} />
          </Box>
        ))}
      </Box>
      <Box sx={{ fontSize: 11.5, color: T.sub, mt: 1 }}>
        Currency comparisons are exact to the cent — a one-cent difference is a
        genuine disagreement.
      </Box>
    </Card>
  );
};

export function NoteReviewPane({ dealId, extract, termsCheck, rejection }) {
  const { loading, json } = useParsedExtract(dealId, extract?.blob_name);

  // terms_check may ride the parsed content or the extract-row descriptor.
  const tc = termsCheck || json?.terms_check || extract?.terms_check || null;
  // Misrouted / illegible → a real rejection (rendered by the base viewer too,
  // but surface it here so the Review tab isn't blank).
  const rej = rejection || json?.rejection || null;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const qualityFlags = [];
  if (json?.scan_quality != null)
    qualityFlags.push({
      label: `Scan: ${humanize(String(json.scan_quality))}`,
      tone: /good|excellent|clear/i.test(String(json.scan_quality))
        ? "ok"
        : "warn",
    });
  if (typeof json?.expected_note_count_met === "boolean")
    qualityFlags.push({
      label: json.expected_note_count_met
        ? "Expected note count met"
        : "Note count mismatch",
      tone: json.expected_note_count_met ? "ok" : "warn",
    });
  const illegible = Array.isArray(json?.illegible_fields)
    ? json.illegible_fields
    : [];

  return (
    <SectionShell>
      <Card
        title="Note verification"
        right={<TermsCheckBadge termsCheck={tc} />}
      >
        {qualityFlags.length || illegible.length ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {qualityFlags.map((f, i) => (
              <Tag key={i} label={f.label} tone={f.tone} />
            ))}
            {illegible.length ? (
              <Tag
                label={`${illegible.length} illegible field${
                  illegible.length === 1 ? "" : "s"
                }`}
                tone="warn"
                title={illegible.join(", ")}
              />
            ) : null}
          </Box>
        ) : (
          <Box sx={{ fontSize: 12.5, color: T.sub }}>
            No scan-quality signals were recorded for this note.
          </Box>
        )}
        {tc == null && (
          <Box sx={{ fontSize: 11.5, color: T.sub, mt: 1 }}>
            No independent terms-check verdict is available for this document.
          </Box>
        )}
      </Card>

      {rej ? (
        <Card title="Rejected">
          <Box sx={{ fontSize: 13, color: red[800] }}>
            {fmtVal(rej.reason || rej)}
          </Box>
        </Card>
      ) : null}

      <NoteDisagreements disagreements={tc?.disagreements} />

      <Box sx={{ fontSize: 11.5, color: T.sub, px: 0.5 }}>
        The full extracted note fields are on the <b>Fields</b> tab. The pages the
        checker reviewed are on the <b>Images</b> tab.
      </Box>
    </SectionShell>
  );
}

// ═══ PHOTO ID ════════════════════════════════════════════════════════════════
const ID_FIELD_ORDER = [
  "borrowerIdType",
  "idNumber",
  "firstName",
  "middleName",
  "lastName",
  "dateOfBirth",
  "idIssueDate",
  "idExpirationDate",
  "issuingCountry",
  "issuingState",
  "citizenshipStatus",
];

const IdCard = ({ title, review }) => {
  const ex = review?.extract || {};
  const flags = [];
  if (review?.requires_re_upload)
    flags.push({
      label: "Re-upload required",
      tone: "danger",
      title: review.re_upload_reason || undefined,
    });
  if (review?.image_quality_rating)
    flags.push({
      label: `Image: ${humanize(String(review.image_quality_rating))}`,
      tone: /excellent|good/i.test(String(review.image_quality_rating))
        ? "ok"
        : "warn",
    });
  if (review?.photo_present === false)
    flags.push({ label: "No photo", tone: "warn" });
  if (review?.photo_obscured === true)
    flags.push({ label: "Photo obscured", tone: "warn" });
  if (review?.legible === false)
    flags.push({ label: "Illegible", tone: "warn" });
  if (ex?.foreignNationalConditionFlag === true)
    flags.push({ label: "Foreign national", tone: "warn" });
  if (review?.is_us_document === false)
    flags.push({ label: "Non-US document", tone: "warn" });

  const orderedKeys = ID_FIELD_ORDER.filter((k) => ex[k] != null && ex[k] !== "");
  const addr = isPlainObject(ex.primaryAddress) ? ex.primaryAddress : null;

  return (
    <Card
      title={title}
      right={
        review?.requires_re_upload ? (
          <Tag
            label="Re-upload required"
            tone="danger"
            title={review.re_upload_reason || undefined}
          />
        ) : null
      }
    >
      {flags.length ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25 }}>
          {flags.map((f, i) => (
            <Tag key={i} label={f.label} tone={f.tone} title={f.title} />
          ))}
        </Box>
      ) : null}

      {review?.requires_re_upload && review?.re_upload_reason ? (
        <Box
          sx={{
            fontSize: 12.5,
            color: red[800],
            background: red[50],
            border: `1px solid ${red[200]}`,
            borderRadius: "8px",
            p: 1,
            mb: 1.25,
          }}
        >
          {review.re_upload_reason}
        </Box>
      ) : null}

      {orderedKeys.map((k) => (
        <Row key={k} label={humanize(k)} value={fmtVal(ex[k])} />
      ))}
      {addr ? (
        <Row
          label="Primary address"
          value={
            addr.formatted ||
            [addr.addressLine1, addr.city, addr.state, addr.zipCode]
              .filter(Boolean)
              .join(", ") ||
            fmtVal(addr)
          }
        />
      ) : null}

      {review?.reviewNotes ? (
        <Box
          sx={{
            mt: 1,
            pt: 1,
            borderTop: `1px solid ${T.line}`,
            fontSize: 12.5,
            color: T.sub,
            fontStyle: "italic",
          }}
        >
          {review.reviewNotes}
        </Box>
      ) : null}
    </Card>
  );
};

export function IdReviewPane({ dealId, extract, images = [] }) {
  const { loading, json } = useParsedExtract(dealId, extract?.blob_name);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (!json) {
    return (
      <SectionShell>
        <Box sx={{ fontSize: 13, color: T.sub }}>
          No structured ID review was extracted from this document.
        </Box>
      </SectionShell>
    );
  }

  const additional = Array.isArray(json.additional_ids) ? json.additional_ids : [];
  const idCount =
    typeof json.id_count === "number" ? json.id_count : 1 + additional.length;

  return (
    <SectionShell>
      {idCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Tag label={`${idCount} IDs in file`} tone="warn" />
        </Box>
      ) : null}

      <IdCard title="Primary ID" review={json} />
      {additional.map((r, i) => (
        <IdCard key={i} title={`Additional ID ${i + 1}`} review={r} />
      ))}

      {images.length ? (
        <Card title="Reviewed images">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
              gap: 1,
            }}
          >
            {images.map((img) => (
              <ImageThumb key={img.blob_name} dealId={dealId} img={img} />
            ))}
          </Box>
        </Card>
      ) : null}
    </SectionShell>
  );
}

// ═══ BACKGROUND REPORT ═══════════════════════════════════════════════════════
const RISK_TONE = {
  none: "ok",
  low: "ok",
  medium: "warn",
  high: "danger",
  critical: "danger",
};

const BgSectionCard = ({ section }) => {
  const risk = String(section?.risk_level || "").toLowerCase();
  const status = String(section?.item_status || "").toLowerCase();
  return (
    <Card
      title={section?.section_name || "Section"}
      right={
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {section?.risk_level ? (
            <Tag
              label={`Risk: ${humanize(risk)}`}
              tone={RISK_TONE[risk] || "neutral"}
            />
          ) : null}
          {section?.item_status ? (
            <Tag
              label={humanize(status)}
              tone={
                status === "resolved" || status === "absent"
                  ? "ok"
                  : status === "open"
                    ? "warn"
                    : "neutral"
              }
            />
          ) : null}
        </Box>
      }
    >
      {section?.loe_required ? (
        <Box sx={{ mb: 1 }}>
          <Tag
            label={`LOE required${
              section.loe_subtype ? ` · ${section.loe_subtype}` : ""
            }`}
            tone="warn"
          />
        </Box>
      ) : null}

      {section?.records_count != null ? (
        <Row label="Records" value={fmtVal(section.records_count)} />
      ) : null}
      {typeof section?.pattern_of_activity === "boolean" ? (
        <Row label="Pattern of activity" value={fmtVal(section.pattern_of_activity)} />
      ) : null}
      {section?.summary_of_section ? (
        <Row label="Summary" value={section.summary_of_section} />
      ) : null}
      {section?.suggested_condition ? (
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            borderRadius: "8px",
            background: amber[50],
            border: `1px solid ${amber[200]}`,
          }}
        >
          <Box
            sx={{ fontSize: 11, fontWeight: 700, color: amber[900], mb: 0.5 }}
          >
            SUGGESTED CONDITION
          </Box>
          <Box sx={{ fontSize: 12.5, color: T.ink }}>
            {section.suggested_condition}
          </Box>
        </Box>
      ) : null}
      {section?.raw_excerpt ? (
        <Box
          sx={{
            mt: 1,
            fontSize: 11.5,
            color: T.sub,
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 120,
            overflowY: "auto",
          }}
        >
          {section.raw_excerpt}
        </Box>
      ) : null}
    </Card>
  );
};

export function BackgroundReviewPane({ dealId, extract }) {
  const { loading, json } = useParsedExtract(dealId, extract?.blob_name);

  const sections = useMemo(
    () =>
      Array.isArray(json?.sections)
        ? json.sections.filter((s) => s?.section_present !== false)
        : [],
    [json],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (!json) {
    return (
      <SectionShell>
        <Box sx={{ fontSize: 13, color: T.sub }}>
          No structured background review was extracted from this document.
        </Box>
      </SectionShell>
    );
  }

  const noMatches = Array.isArray(json.sections_with_no_matches)
    ? json.sections_with_no_matches
    : [];

  return (
    <SectionShell>
      {json.backgroundReportSummary || json.background_report_issuer ? (
        <Card title="Background report">
          {json.background_report_issuer ? (
            <Row label="Issuer" value={fmtVal(json.background_report_issuer)} />
          ) : null}
          {json.backgroundReportSummary ? (
            <Row label="Summary" value={fmtVal(json.backgroundReportSummary)} />
          ) : null}
        </Card>
      ) : null}

      {sections.length ? (
        sections.map((s, i) => <BgSectionCard key={i} section={s} />)
      ) : (
        <Box sx={{ fontSize: 13, color: T.sub, px: 0.5 }}>
          No risk sections with records were found.
        </Box>
      )}

      {noMatches.length ? (
        <Box sx={{ fontSize: 11.5, color: T.sub, px: 0.5 }}>
          No matches: {noMatches.join(", ")}
        </Box>
      ) : null}
    </SectionShell>
  );
}

// ── dispatcher ───────────────────────────────────────────────────────────────
export function ReviewPane({ kind, dealId, extract, images }) {
  if (kind === "id")
    return <IdReviewPane dealId={dealId} extract={extract} images={images} />;
  if (kind === "background")
    return <BackgroundReviewPane dealId={dealId} extract={extract} />;
  if (kind === "note")
    return <NoteReviewPane dealId={dealId} extract={extract} />;
  return null;
}
